import { useEffect, useRef, useState, type RefObject } from "react"

import { chattingCopy } from "../config/chat-copy"
import { MESSAGE_ROLE, type ChatMessages as ChatMessagesType } from "../types"
import { TypingIndicator } from "./typing-indicator"

import Bullet from "@/assets/img/live-chat/bullet.svg?react"
import PrivateShield from "@/assets/svg/advisors/private-shield.svg?react"
import StatusSend from "@/assets/svg/advisors/status-send-v4.svg?react"
import StatusSeen from "@/assets/svg/advisors/status-seen-v4.svg?react"
import SectionBg from "@/assets/img/live-chat/advisor-section-background.png"

interface ChatMessagesProps {
  messages: ChatMessagesType
  isTyping: boolean
  scrollRef: RefObject<HTMLDivElement | null>
}

export function ChatMessages({ messages, isTyping, scrollRef }: ChatMessagesProps) {
  const lastUserIndex = messages.map((m) => m.role).lastIndexOf(MESSAGE_ROLE.USER)
  const [readStatus, setReadStatus] = useState<"send" | "seen">("seen")
  const prevLastUserRef = useRef<number>(-1)

  // When a new user message becomes the last one, show "send" then flip to "seen".
  useEffect(() => {
    if (lastUserIndex === prevLastUserRef.current) return
    prevLastUserRef.current = lastUserIndex
    const last = messages[messages.length - 1]
    /* eslint-disable react-hooks/set-state-in-effect */
    if (last?.role === MESSAGE_ROLE.USER) {
      setReadStatus("send")
      const t = setTimeout(() => setReadStatus("seen"), 6000)
      return () => clearTimeout(t)
    }
    setReadStatus("seen")
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [lastUserIndex, messages])

  // Only auto-scroll to the latest message when the user is already at the
  // bottom. If they've scrolled up to read a long reply, don't yank them down
  // on the next messages/history/typing update.
  const stickToBottomRef = useRef(true)

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  useEffect(() => {
    if (!stickToBottomRef.current) return
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" })
  }, [messages, isTyping, scrollRef])

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="no-scrollbar flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-5 py-5"
    >
      {/* Intro card */}
      <div className="relative w-full shrink-0 self-center overflow-hidden rounded-[20px] bg-gradient-to-l from-purple-light/10 to-purple-accent/20 p-4">
        <img
          src={SectionBg}
          alt=""
          className="pointer-events-none absolute right-0 top-0 h-[175px] w-[120px] object-contain"
        />
        <p className="mb-2 font-display text-xl font-medium text-purple-light">
          {chattingCopy.title}
        </p>
        <div className="flex flex-col gap-2.5 font-body text-base text-purple-light/70 [&_svg]:min-h-6 [&_svg]:min-w-6">
          {chattingCopy.features.map((f) => (
            <div key={f} className="flex items-center gap-2">
              <Bullet />
              {f}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <PrivateShield />
            <span className="font-semibold text-purple-light">{chattingCopy.private}</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {messages.map((message, index) => {
        const isUser = message.role === MESSAGE_ROLE.USER
        const isLastUser = isUser && index === lastUserIndex
        return (
          <div
            key={index}
            className={
              isUser
                ? "relative ml-auto max-w-[75%] rounded-xl bg-[#463377] px-4 py-2.5 text-white"
                : "max-w-[75%] self-start rounded-xl bg-purple-light/10 px-4 py-2.5 text-purple-light"
            }
          >
            <p className="whitespace-pre-wrap font-body text-base leading-5">{message.content}</p>
            {isLastUser && (
              <span className="mt-1 flex justify-end [&_svg]:h-3.5 [&_svg]:w-4">
                {readStatus === "send" ? <StatusSend /> : <StatusSeen />}
              </span>
            )}
          </div>
        )
      })}

      {isTyping && <TypingIndicator />}
    </div>
  )
}
